/**
 * OverviewTab — LONG LIST stage
 * Core identification, classification, market context, regulatory, external links, assignment
 * S9-12: Title and description inline-editable, Pick for Review wired to ALLOWED_TRANSITIONS
 */

import { useCallback, useState } from 'react'
import type { ProjectDetailProject } from '../../../types'
import { InlineNote } from '../../ui/InlineNote'
import { CompletableSection } from '../../ui/CompletableSection'
import type { SectionCompletionState } from '../../../hooks/useSectionCompletions'

const TRL_OPTIONS = [
  { value: '0', label: '0 - Idea' },
  { value: '1', label: '1 - Basic Research' },
  { value: '2', label: '2 - Concept' },
  { value: '3', label: '3 - Proof of Concept' },
  { value: '4', label: '4 - Lab Demonstration' },
  { value: '5', label: '5 - Pilot' },
  { value: '6', label: '6 - Prototype' },
  { value: '7', label: '7 - Pre-production' },
  { value: '8', label: '8 - Production Ready' },
  { value: '9', label: '9 - Deployed' },
]

const MARKET_SIZE_OPTIONS = [
  { value: 'Niche', label: 'Niche (<$100M)' },
  { value: 'Small', label: 'Small ($100M-$500M)' },
  { value: 'Medium', label: 'Medium ($500M-$2B)' },
  { value: 'Large', label: 'Large ($2B-$10B)' },
  { value: 'Massive', label: 'Massive (>$10B)' },
]

const REGULATORY_FRAMEWORKS = [
  { value: 'FDA', label: 'FDA (US)' },
  { value: 'EMA', label: 'EMA (EU)' },
  { value: 'PMDA', label: 'PMDA (Japan)' },
  { value: 'CE', label: 'CE Mark' },
  { value: 'None', label: 'No Regulatory Path' },
]

const REGULATORY_COMPLEXITY = [
  { value: 'Low', label: 'Low Complexity' },
  { value: 'Moderate', label: 'Moderate' },
  { value: 'High', label: 'High Complexity' },
]

const LINK_TYPES = [
  'University',
  'PubMed',
  'Patent',
  'ClinicalTrials',
  'Other',
]

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

export function OverviewTab({
  project,
  projectId,
  domain,
  onFieldChange,
  sectionCompletions: sc,
  tabId = 'tab-overview',
}: TabContentProps) {
  // Section completion helpers
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId, sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId, sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId, sectionId)

  // S9-hotfix6: pipeline state moved to ProjectActionBar at ProjectDetail level
  const [optimisticErrors, setOptimisticErrors] = useState<Record<string, string>>({})

  const [externalLinks, setExternalLinks] = useState<
    { type: string; url: string }[]
  >(
    project.external_links?.map((link) => ({
      type: link.type || 'Other',
      url: link.url || '',
    })) || []
  )

  const [newLink, setNewLink] = useState({ type: 'Other', url: '' })

  const handleAddLink = () => {
    if (newLink.url.trim()) {
      const updated = [...externalLinks, newLink]
      setExternalLinks(updated)
      onFieldChange('external_links', updated)
      setNewLink({ type: 'Other', url: '' })
    }
  }

  const handleRemoveLink = (index: number) => {
    const updated = externalLinks.filter((_, i) => i !== index)
    setExternalLinks(updated)
    onFieldChange('external_links', updated)
  }

  // Handle field change with optimistic error clearing
  const handleFieldChangeWithErrorClearing = (field: string, value: any) => {
    // Clear error for this field if it exists
    setOptimisticErrors(prev => {
      const next = { ...prev }
      delete next[field]
      return next
    })
    onFieldChange(field, value)
  }

  // S9-hotfix6: handlePickForReview removed; transitions now go through ProjectActionBar.

  return (
    <div className="space-y-6">
      {/* Core Identification */}
      <CompletableSection
        sectionId="ov-core-identification"
        title="Core Identification"
        isComplete={isComplete('ov-core-identification')}
        completedByName={getInfo('ov-core-identification')?.completed_by_name}
        completedAt={getInfo('ov-core-identification')?.completed_at}
        onToggle={() => toggle('ov-core-identification')}
      >

        {/* Title — Inline editable via onChange */}
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Title <span className="text-[#E61B25]">*</span>
          </label>
          <input
            type="text"
            value={project.title || ''}
            onChange={(e) => handleFieldChangeWithErrorClearing('title', e.target.value)}
            placeholder="Project title"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
          {optimisticErrors.title && (
            <div className="mt-2 text-[12px] text-[#E61B25]">
              {optimisticErrors.title}
            </div>
          )}
        </div>

        {/* Description — Inline editable via InlineNote */}
        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Description
          </label>
          <InlineNote
            value={project.description || ''}
            onChange={(value) => handleFieldChangeWithErrorClearing('description', value)}
            placeholder="Project overview and key details"
            minHeight={100}
            editable={true}
          />
          {optimisticErrors.description && (
            <div className="mt-2 text-[12px] text-[#E61B25]">
              {optimisticErrors.description}
            </div>
          )}
        </div>
      </CompletableSection>

      {/* Classification */}
      <CompletableSection
        sectionId="ov-classification"
        title="Classification"
        isComplete={isComplete('ov-classification')}
        completedByName={getInfo('ov-classification')?.completed_by_name}
        completedAt={getInfo('ov-classification')?.completed_at}
        onToggle={() => toggle('ov-classification')}
      >

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Industry Tags
          </label>
          <div className="text-[12px] text-[#a8a8c0]">
            Multi-select autocomplete (placeholder - would show existing tags)
          </div>
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Initial TRL Estimate
          </label>
          <select
            value={project.trl?.toString() || ''}
            onChange={(e) => onFieldChange('trl', parseInt(e.target.value, 10))}
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select TRL level</option>
            {TRL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </CompletableSection>

      {/* Market Context */}
      <CompletableSection
        sectionId="ov-market-context"
        title="Market Context"
        isComplete={isComplete('ov-market-context')}
        completedByName={getInfo('ov-market-context')?.completed_by_name}
        completedAt={getInfo('ov-market-context')?.completed_at}
        onToggle={() => toggle('ov-market-context')}
      >

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Problem / Unmet Need
          </label>
          <InlineNote
            value={project.problem_statement || ''}
            onChange={(value) => onFieldChange('problem_statement', value)}
            placeholder="What problem does this solve?"
            minHeight={80}
          />
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Target Customer
          </label>
          <input
            type="text"
            value={project.target_customer || ''}
            onChange={(e) => onFieldChange('target_customer', e.target.value)}
            placeholder="Who are the primary customers?"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Market Size Estimate
          </label>
          <select
            value={project.market_size || ''}
            onChange={(e) => onFieldChange('market_size', e.target.value)}
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select market size</option>
            {MARKET_SIZE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </CompletableSection>

      {/* Regulatory Landscape */}
      <CompletableSection
        sectionId="ov-regulatory"
        title="Regulatory Landscape"
        isComplete={isComplete('ov-regulatory')}
        completedByName={getInfo('ov-regulatory')?.completed_by_name}
        completedAt={getInfo('ov-regulatory')?.completed_at}
        onToggle={() => toggle('ov-regulatory')}
      >

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Regulatory Framework
          </label>
          <select
            value={project.regulatory_framework || ''}
            onChange={(e) => onFieldChange('regulatory_framework', e.target.value)}
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select framework</option>
            {REGULATORY_FRAMEWORKS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Regulatory Complexity
          </label>
          <select
            value={project.regulatory_complexity || ''}
            onChange={(e) =>
              onFieldChange('regulatory_complexity', e.target.value)
            }
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select complexity</option>
            {REGULATORY_COMPLEXITY.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Regulatory Notes
          </label>
          <InlineNote
            value={project.regulatory_notes || ''}
            onChange={(value) => onFieldChange('regulatory_notes', value)}
            placeholder="Key regulatory considerations"
            minHeight={80}
          />
        </div>
      </CompletableSection>

      {/* External Links */}
      <CompletableSection
        sectionId="ov-external-links"
        title="External Links"
        isComplete={isComplete('ov-external-links')}
        completedByName={getInfo('ov-external-links')?.completed_by_name}
        completedAt={getInfo('ov-external-links')?.completed_at}
        onToggle={() => toggle('ov-external-links')}
      >

        {externalLinks.length > 0 && (
          <div className="mb-4 space-y-2">
            {externalLinks.map((link, idx) => (
              <div
                key={idx}
                className="
                  flex items-center justify-between
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg p-3
                "
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-[#a8a8c0] mb-1">
                    {link.type}
                  </div>
                  <div className="text-[12px] text-[#8b8ba0] truncate">
                    {link.url}
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveLink(idx)}
                  className="
                    ml-2 text-[12px] text-[#E61B25]
                    hover:text-[#d41620]
                    font-medium whitespace-nowrap
                  "
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <select
            value={newLink.type}
            onChange={(e) => setNewLink({ ...newLink, type: e.target.value })}
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            {LINK_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <input
            type="url"
            placeholder="https://..."
            value={newLink.url}
            onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
          <button
            onClick={handleAddLink}
            className="
              w-full px-4 py-2
              bg-[#12122a] border border-dashed border-[#2a2a44]
              rounded-lg
              text-[13px] font-medium text-[#a8a8c0]
              hover:border-[#3a3a54] hover:text-[#e8e8f0]
              transition-colors
            "
          >
            + Add Link
          </button>
        </div>
      </CompletableSection>

      {/*
        S9-hotfix6: per-tab "Pick for Review" panel removed.
        Stage transitions are now handled by the global ProjectActionBar
        rendered at the ProjectDetail level (visible from every tab).
      */}
    </div>
  )
}

export default OverviewTab
